import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconBuildingFactory2,
  IconCheck,
  IconScale,
  IconScan,
} from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";

/**
 * Mill · Weighbridge Intake + Ramp Grading (Borang Penggredan digital)
 *
 * Left pane · Weighbridge Intake
 *   - Scan an NFC delivery card written at the estate
 *   - Card manifest auto-fills driver / vehicle / estate / division / block /
 *     bunch count and grading summary
 *   - Capture gross + tare + pre-sample rejects
 *   - Sets base extraction rate (BOER/BKER) by avg bunch weight (Jadual 1)
 *
 * Right pane · Ramp Grading (Borang Penggredan)
 *   - Pick an active ticket
 *   - Enter first grading (Masak / Mengkal / Busuk / Kosong)
 *   - Enter second grading (Kotor / Lama / Dura / Tangkai Panjang)
 *   - Enter wet-load %s (Muatan Basah / Tandan Tidak Segar)
 *   - Server previews the penalty breakdown live per MPOB Jadual 3-10
 *   - Save commits + closes the ticket with computed GOER/GKER
 */

interface Mill {
  id: string;
  name: string;
}
interface WeighbridgeTicket {
  id: string;
  ticketNumber: string;
  status: string;
  vendorName: string | null;
  vendorMpobLicense: string | null;
  vehicleRegistration: string | null;
  driverName: string | null;
  driverIdNumber: string | null;
  grossWeightKg: string | null;
  tareWeightKg: string | null;
  preSampleRejectKg: string;
  netBeforeReturnKg: string | null;
  netAfterReturnKg: string | null;
  avgBunchWeightKg: string | null;
  boerPct: string | null;
  bkerPct: string | null;
  totalPenaltyPct: string | null;
  goerPct: string | null;
  gkerPct: string | null;
  consignmentRejected: string | null;
  consignmentRejectReason: string | null;
  gradedAt: string | null;
  createdAt: string;
  nfcCardId: string | null;
}

interface PenaltyPreview {
  perClassPct: Record<string, number>;
  totalPenaltyPct: number;
  goerPct: number;
  gkerPct: number;
  consignmentRejected: boolean;
  rejectRuleFired: string | null;
  rejectReason: string | null;
}

export function Mill() {
  const qc = useQueryClient();
  const millsQ = useQuery({
    queryKey: ["mills"],
    queryFn: () => api.list<Mill>("/v1/mills"),
  });
  const ticketsQ = useQuery({
    queryKey: ["weighbridge-tickets"],
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => api.list<WeighbridgeTicket>("/v1/weighbridge-tickets"),
  });

  const mill = millsQ.data?.data?.[0]; // First mill for now
  const tickets = ticketsQ.data?.data ?? [];

  // ─── Weighbridge Intake (left pane) ────────────────────────────
  const [scanCode, setScanCode] = useState("");
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [gross, setGross] = useState<number | "">("");
  const [tare, setTare] = useState<number | "">("");
  const [preReject, setPreReject] = useState<number | "">("");
  const [avgBunchKg, setAvgBunchKg] = useState<number | "">(20);
  // Editable BOER / BKER · empty means "use Jadual 1 lookup from avg bunch weight".
  const [boerOverride, setBoerOverride] = useState<number | "">("");
  const [bkerOverride, setBkerOverride] = useState<number | "">("");
  // Pre-sample rejected bunches (returned to estate or discarded) —
  // physically removed BEFORE the sample is drawn. Kg goes into
  // preSampleRejectKg; the per-category counts go into the JSONB
  // preSampleRejectBreakdown so downstream can audit what was rejected.
  const [rejUnripe, setRejUnripe] = useState<number | "">("");
  const [rejPeramRotten, setRejPeramRotten] = useState<number | "">("");
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const selectedTicket = tickets.find((t) => t.id === selectedTicketId) ?? null;

  const scanCard = async () => {
    setScanMsg(null);
    if (!mill) {
      setScanMsg("No mill configured. Add one under Estate Land.");
      return;
    }
    try {
      const res = await api.post<{
        data: WeighbridgeTicket;
        card: unknown;
      }>("/v1/weighbridge-tickets/scan", {
        nfcCardCode: scanCode.trim(),
        millId: mill.id,
      });
      // The scan endpoint wraps data under `.data.data` because
      // the client wrapper unwraps once already.
      const rec = (res.data as unknown as { data: WeighbridgeTicket; card: unknown });
      const ticket = (rec.data ?? res.data) as WeighbridgeTicket;
      setSelectedTicketId(ticket.id);
      setScanMsg(
        `Scanned ✓ · ticket ${ticket.ticketNumber} · ${ticket.vendorName ?? "(no vendor)"}`,
      );
      setScanCode("");
      qc.invalidateQueries({ queryKey: ["weighbridge-tickets"] });
      qc.invalidateQueries({ queryKey: ["nfc-delivery-cards"] });
    } catch (err) {
      setScanMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  const saveWeights = async () => {
    if (!selectedTicket) return;
    try {
      // Only include the per-category breakdown when at least one is set.
      const breakdown: Record<string, number> = {};
      if (rejUnripe !== "" && Number(rejUnripe) > 0)
        breakdown.unripe = Number(rejUnripe);
      if (rejPeramRotten !== "" && Number(rejPeramRotten) > 0)
        breakdown.buah_peram_rotten = Number(rejPeramRotten);

      await api.patch(`/v1/weighbridge-tickets/${selectedTicket.id}/weigh`, {
        grossWeightKg: gross === "" ? undefined : Number(gross),
        tareWeightKg: tare === "" ? undefined : Number(tare),
        preSampleRejectKg: preReject === "" ? undefined : Number(preReject),
        preSampleRejectBreakdown:
          Object.keys(breakdown).length > 0 ? breakdown : undefined,
        baseRateBasis: "AVG_BUNCH_WEIGHT",
        avgBunchWeightKg: avgBunchKg === "" ? undefined : Number(avgBunchKg),
        boerPctOverride: boerOverride === "" ? undefined : Number(boerOverride),
        bkerPctOverride: bkerOverride === "" ? undefined : Number(bkerOverride),
      });
      setScanMsg("Weights + base rate saved ✓");
      setGross("");
      setTare("");
      setPreReject("");
      qc.invalidateQueries({ queryKey: ["weighbridge-tickets"] });
    } catch (err) {
      setScanMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  // ─── Ramp Grading (right pane) — Borang Penggredan ─────────────
  const [g, setG] = useState({
    sampleSize: 100,
    // Masak is DERIVED (sampleSize − total defects) and rendered read-only.
    // Stored in state only because the POST/preview API expects the field.
    masak: 0,
    mengkal: 0,
    busuk: 0,
    kosong: 0,
    kotor: 0,
    lama: 0,
    dura: 0,
    tangkaiPanjang: 0,
    unfreshCount: 0,
    wetCount: 0,
  });

  // MPOB sample-size rule: 100 if net-weight > 5 tons, else 50.
  // Auto-applied when the selected ticket's net weight is known; the
  // grader can still override manually below.
  const suggestedSampleSize = useMemo(() => {
    const netKg = Number(selectedTicket?.netAfterReturnKg ?? 0);
    if (!netKg) return null;
    return netKg > 5000 ? 100 : 50;
  }, [selectedTicket?.netAfterReturnKg]);
  useEffect(() => {
    if (suggestedSampleSize && suggestedSampleSize !== g.sampleSize) {
      setG((prev) => ({ ...prev, sampleSize: suggestedSampleSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedSampleSize]);

  // Masak derivation · sample size minus the sum of every defect class.
  // Any change to a defect count immediately updates masak so the total
  // always equals sampleSize.
  const derivedMasak = useMemo(() => {
    const defectSum =
      g.mengkal + g.busuk + g.kosong + g.kotor + g.lama + g.dura + g.tangkaiPanjang;
    return Math.max(0, g.sampleSize - defectSum);
  }, [g.sampleSize, g.mengkal, g.busuk, g.kosong, g.kotor, g.lama, g.dura, g.tangkaiPanjang]);
  useEffect(() => {
    if (derivedMasak !== g.masak) {
      setG((prev) => ({ ...prev, masak: derivedMasak }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedMasak]);
  const [preview, setPreview] = useState<PenaltyPreview | null>(null);
  const [gradeMsg, setGradeMsg] = useState<string | null>(null);

  // ── AI Ramp Grading (photo of pile after truck unloads) ─────────
  const [aiRampBusy, setAiRampBusy] = useState(false);
  const [aiRampMsg, setAiRampMsg] = useState<string | null>(null);
  const [aiRampResult, setAiRampResult] = useState<{
    prediction: {
      sample_size: number;
      masak: number;
      mengkal: number;
      busuk: number;
      kosong: number;
      kotor: number;
      lama: number;
      dura: number;
      tangkai_panjang: number;
      unfresh: number;
      wet: number;
      confidence: number;
    };
    model: string;
    latencyMs: number;
    photoUrl: string | null;
  } | null>(null);
  const [aiRampPhotoPreview, setAiRampPhotoPreview] = useState<string | null>(
    null,
  );
  const aiRampFileRef = useRef<HTMLInputElement>(null);

  const runAiRampGrade = async (file: File) => {
    if (!selectedTicket) {
      setAiRampMsg("Pick a ticket first.");
      return;
    }
    setAiRampMsg(null);
    setAiRampResult(null);
    setAiRampPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setAiRampBusy(true);
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(String(reader.result).split(",")[1] ?? "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.post<{
        prediction: typeof aiRampResult extends null
          ? never
          : NonNullable<typeof aiRampResult>["prediction"];
        model: string;
        latencyMs: number;
        photoUrl: string | null;
      }>("/v1/mill-grading-results/ai", {
        weighbridgeTicketId: selectedTicket.id,
        photoBase64: b64,
        mimeType: file.type || "image/jpeg",
        sampleSize: g.sampleSize,
      });
      setAiRampResult(res.data);
      setAiRampMsg(
        `AI graded ✓ · ${res.data.model} · confidence ${(res.data.prediction.confidence * 100).toFixed(0)}% · ${res.data.latencyMs} ms`,
      );
    } catch (err) {
      setAiRampMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setAiRampBusy(false);
    }
  };

  const applyAiToGrading = () => {
    if (!aiRampResult) return;
    const p = aiRampResult.prediction;
    // Masak stays derived · we only load the DEFECT classes from the AI.
    // Sample size honours the vehicle-tonnage rule when available.
    setG((prev) => ({
      ...prev,
      sampleSize: suggestedSampleSize ?? p.sample_size,
      mengkal: p.mengkal,
      busuk: p.busuk,
      kosong: p.kosong,
      kotor: p.kotor,
      lama: p.lama,
      dura: p.dura,
      tangkaiPanjang: p.tangkai_panjang,
      unfreshCount: p.unfresh,
      wetCount: p.wet,
    }));
    setAiRampMsg(
      "Defect counts applied · Masak auto-derives to keep total = sample size",
    );
  };

  const totalCounted =
    g.masak + g.mengkal + g.busuk + g.kosong + g.kotor + g.lama + g.dura + g.tangkaiPanjang;

  // Debounced live preview call
  useEffect(() => {
    if (!selectedTicket || !selectedTicket.boerPct) return;
    const handle = setTimeout(async () => {
      try {
        const res = await api.post<PenaltyPreview>(
          "/v1/mill-grading-results/preview",
          {
            weighbridgeTicketId: selectedTicket.id,
            sampleSize: g.sampleSize,
            masakCount: g.masak,
            mengkalCount: g.mengkal,
            busukCount: g.busuk,
            kosongCount: g.kosong,
            kotorCount: g.kotor,
            lamaCount: g.lama,
            duraCount: g.dura,
            tangkaiPanjangCount: g.tangkaiPanjang,
            unfreshCount: g.unfreshCount,
            wetCount: g.wetCount,
          },
        );
        setPreview(res.data);
      } catch (err) {
        // Preview failure = ignore silently, don't spam user
        console.warn("preview failed:", err);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id, selectedTicket?.boerPct, g]);

  const saveGrading = async () => {
    if (!selectedTicket) return;
    setGradeMsg(null);
    try {
      const res = await api.post<{ computed: PenaltyPreview }>(
        "/v1/mill-grading-results",
        {
          weighbridgeTicketId: selectedTicket.id,
          sampleSize: g.sampleSize,
          masakCount: g.masak,
          mengkalCount: g.mengkal,
          busukCount: g.busuk,
          kosongCount: g.kosong,
          kotorCount: g.kotor,
          lamaCount: g.lama,
          duraCount: g.dura,
          tangkaiPanjangCount: g.tangkaiPanjang,
          unfreshCount: g.unfreshCount,
          wetCount: g.wetCount,
        },
      );
      const c = res.data.computed;
      setGradeMsg(
        c.consignmentRejected
          ? `Consignment REJECTED (${c.rejectReason}) — rule ${c.rejectRuleFired}`
          : `Graded ✓ · penalty ${c.totalPenaltyPct.toFixed(2)}% · GOER ${c.goerPct.toFixed(2)}%`,
      );
      qc.invalidateQueries({ queryKey: ["weighbridge-tickets"] });
    } catch (err) {
      setGradeMsg(err instanceof ApiError ? err.message : String(err));
    }
  };

  const activeTickets = useMemo(
    () =>
      tickets.filter(
        (t) => t.status !== "RECONCILED" && t.status !== "SYNCED_TO_WBS",
      ),
    [tickets],
  );

  return (
    <div className="space-y-5 text-sm">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div>
        <h1 className="flex items-center gap-2 text-lg font-medium">
          <IconBuildingFactory2 size={20} className="text-slate-700" />
          Mill · Weighbridge &amp; Ramp Grading
        </h1>
        <p className="text-xs text-text-secondary">
          {mill ? (
            <>
              Operating mill: <b>{mill.name}</b>. Scan NFC delivery cards at the
              gate, capture weights, then run Borang Penggredan grading at the
              ramp.
            </>
          ) : (
            <span className="text-amber-800">
              No mill configured. Add one under Estate Land → Mills before using
              this page.
            </span>
          )}
        </p>
      </div>

      {/* ─── Daily KPI tiles ────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi
          icon={<IconScan size={16} className="text-blue-700" />}
          label="Tickets today"
          value={String(tickets.length)}
          hint={`${activeTickets.length} open`}
        />
        <Kpi
          icon={<IconCheck size={16} className="text-emerald-700" />}
          label="Graded"
          value={String(
            tickets.filter((t) => t.status === "GRADED" || t.status === "RECONCILED")
              .length,
          )}
          hint="First+second grading complete"
        />
        <Kpi
          icon={<IconAlertTriangle size={16} className="text-red-700" />}
          label="Rejected consignments"
          value={String(tickets.filter((t) => t.status === "REJECTED").length)}
          hint="Busuk>30 / Kosong>20 / Kotor>30 bunches"
        />
        <Kpi
          icon={<IconScale size={16} className="text-emerald-700" />}
          label="Avg GOER today"
          value={
            (() => {
              const g = tickets
                .map((t) => Number(t.goerPct))
                .filter((n) => n > 0);
              return g.length ? `${(g.reduce((a, b) => a + b, 0) / g.length).toFixed(2)}%` : "—";
            })()
          }
          hint={`vs. base BOER 21.0%`}
        />
      </div>

      {/* ─── Two-column layout ─────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ── LEFT · Weighbridge Intake ───────────────────────── */}
        <section className="rounded border border-border bg-surface-2">
          <div className="border-b border-border px-3 py-2 font-medium">
            Weighbridge Intake
          </div>
          <div className="space-y-3 p-3">
            <div className="rounded bg-surface-0 p-2 text-[11px] text-text-secondary">
              Scan the NFC delivery card the driver hands over. Ticket
              auto-fills from the estate-side grading manifest.
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={scanCode}
                onChange={(e) => setScanCode(e.target.value)}
                placeholder="Paste or type NFC-DLV-YYYY-MM-DD-NNNN"
                className="flex-1 rounded border border-border bg-surface-0 px-2 py-1.5 text-sm font-mono"
              />
              <button
                onClick={scanCard}
                disabled={!scanCode.trim() || !mill}
                className="rounded bg-text-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
              >
                Scan
              </button>
            </div>
            {scanMsg && (
              <div
                className={`rounded p-2 text-xs ${
                  scanMsg.includes("✓")
                    ? "border border-emerald-300 bg-emerald-50 text-emerald-800"
                    : "border border-amber-300 bg-amber-50 text-amber-900"
                }`}
              >
                {scanMsg}
              </div>
            )}

            {selectedTicket && (
              <div className="rounded border border-border bg-surface-0 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">
                    {selectedTicket.ticketNumber}
                  </div>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                    {selectedTicket.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <div className="text-text-secondary">Vendor</div>
                    <div className="font-medium">
                      {selectedTicket.vendorName ?? "—"}
                    </div>
                    {selectedTicket.vendorMpobLicense && (
                      <div className="font-mono text-[10px] text-text-secondary">
                        MPOB {selectedTicket.vendorMpobLicense}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-text-secondary">Vehicle</div>
                    <div className="font-medium">
                      {selectedTicket.vehicleRegistration ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-secondary">Driver</div>
                    <div className="font-medium">
                      {selectedTicket.driverName ?? "—"}
                    </div>
                    {selectedTicket.driverIdNumber && (
                      <div className="font-mono text-[10px] text-text-secondary">
                        IC {selectedTicket.driverIdNumber}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-text-secondary">NFC card</div>
                    <div className="font-mono text-[10px]">
                      {selectedTicket.nfcCardId ? "linked" : "manual entry"}
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <label className="text-[10px] text-text-secondary">
                    Gross (kg)
                    <input
                      type="number"
                      value={gross}
                      onChange={(e) =>
                        setGross(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder={selectedTicket.grossWeightKg ?? ""}
                      className="mt-0.5 w-full rounded border border-border bg-surface-1 px-2 py-1 text-right text-sm font-mono"
                    />
                  </label>
                  <label className="text-[10px] text-text-secondary">
                    Tare (kg)
                    <input
                      type="number"
                      value={tare}
                      onChange={(e) =>
                        setTare(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder={selectedTicket.tareWeightKg ?? ""}
                      className="mt-0.5 w-full rounded border border-border bg-surface-1 px-2 py-1 text-right text-sm font-mono"
                    />
                  </label>
                  <label className="text-[10px] text-text-secondary">
                    Returns (kg)
                    <input
                      type="number"
                      value={preReject}
                      onChange={(e) =>
                        setPreReject(
                          e.target.value === "" ? "" : Number(e.target.value),
                        )
                      }
                      placeholder={selectedTicket.preSampleRejectKg}
                      className="mt-0.5 w-full rounded border border-border bg-surface-1 px-2 py-1 text-right text-sm font-mono"
                    />
                  </label>
                </div>

                <label className="mt-2 block text-[10px] text-text-secondary">
                  Avg bunch weight (kg) · sets base BOER/BKER per Jadual 1
                  <input
                    type="number"
                    value={avgBunchKg}
                    onChange={(e) =>
                      setAvgBunchKg(
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    className="mt-0.5 w-full rounded border border-border bg-surface-1 px-2 py-1 text-sm"
                  />
                </label>

                {(selectedTicket.boerPct || selectedTicket.netBeforeReturnKg) && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    {selectedTicket.netBeforeReturnKg && (
                      <div>
                        <div className="text-text-secondary">Net before returns</div>
                        <div className="font-mono font-bold">
                          {Number(selectedTicket.netBeforeReturnKg).toLocaleString()} kg
                        </div>
                      </div>
                    )}
                    {selectedTicket.netAfterReturnKg && (
                      <div>
                        <div className="text-text-secondary">Net after returns</div>
                        <div className="font-mono font-bold text-emerald-800">
                          {Number(selectedTicket.netAfterReturnKg).toLocaleString()} kg
                        </div>
                      </div>
                    )}
                    {selectedTicket.boerPct && (
                      <div>
                        <div className="text-text-secondary">BOER (base)</div>
                        <div className="font-mono font-bold">
                          {selectedTicket.boerPct}%
                        </div>
                      </div>
                    )}
                    {selectedTicket.bkerPct && (
                      <div>
                        <div className="text-text-secondary">BKER (base)</div>
                        <div className="font-mono font-bold">
                          {selectedTicket.bkerPct}%
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Editable BOER / BKER · overrides the Jadual 1 lookup */}
                <div className="mt-3 rounded border border-brand-green/40 bg-brand-green/5 p-2 space-y-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-brand-green-dark">
                    Base rate override (optional)
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <label className="text-text-secondary">
                      BOER %{" "}
                      {selectedTicket.boerPct && (
                        <span className="text-text-muted">
                          (Jadual: {selectedTicket.boerPct}%)
                        </span>
                      )}
                      <input
                        type="number"
                        step="0.01"
                        placeholder={selectedTicket.boerPct ?? "e.g. 21.00"}
                        value={boerOverride}
                        onChange={(e) =>
                          setBoerOverride(
                            e.target.value === "" ? "" : Number(e.target.value),
                          )
                        }
                        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-text-secondary">
                      BKER %{" "}
                      {selectedTicket.bkerPct && (
                        <span className="text-text-muted">
                          (Jadual: {selectedTicket.bkerPct}%)
                        </span>
                      )}
                      <input
                        type="number"
                        step="0.01"
                        placeholder={selectedTicket.bkerPct ?? "e.g. 5.00"}
                        value={bkerOverride}
                        onChange={(e) =>
                          setBkerOverride(
                            e.target.value === "" ? "" : Number(e.target.value),
                          )
                        }
                        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                      />
                    </label>
                  </div>
                  <div className="text-[9px] text-text-secondary">
                    Leave blank to keep the auto value from avg bunch weight.
                  </div>
                </div>

                {/* Returned / rejected bunches · pre-sample (per category) */}
                <div className="mt-2 rounded border border-red-300 bg-red-50 p-2 space-y-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-red-800">
                    Returned / rejected bunches (pre-sample)
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <label className="text-text-secondary">
                      Unripe (bunches)
                      <input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={rejUnripe}
                        onChange={(e) =>
                          setRejUnripe(
                            e.target.value === "" ? "" : Math.max(0, Number(e.target.value)),
                          )
                        }
                        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-text-secondary">
                      Buah peram / rotten (bunches)
                      <input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={rejPeramRotten}
                        onChange={(e) =>
                          setRejPeramRotten(
                            e.target.value === "" ? "" : Math.max(0, Number(e.target.value)),
                          )
                        }
                        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                      />
                    </label>
                  </div>
                  <div className="text-[9px] text-text-secondary">
                    Physically removed from the load before sampling. Stored
                    alongside the returned-kg total for audit.
                  </div>
                </div>

                <button
                  onClick={saveWeights}
                  className="mt-3 w-full rounded bg-emerald-700 py-1.5 text-xs font-bold text-white hover:bg-emerald-800"
                >
                  Save weights + base rate
                </button>
              </div>
            )}

            {/* Recent tickets list */}
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                Recent tickets · pick to grade
              </div>
              <div className="max-h-60 space-y-1 overflow-y-auto">
                {tickets.slice(0, 20).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTicketId(t.id)}
                    className={`flex w-full items-center justify-between rounded border px-2 py-1.5 text-left text-[11px] ${
                      selectedTicketId === t.id
                        ? "border-blue-500 bg-blue-50"
                        : "border-border bg-surface-0 hover:bg-surface-1"
                    }`}
                  >
                    <span className="font-mono">{t.ticketNumber}</span>
                    <span className="truncate text-text-secondary">
                      {t.vendorName ?? "—"}
                    </span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                        t.status === "GRADED"
                          ? "bg-emerald-100 text-emerald-800"
                          : t.status === "REJECTED"
                            ? "bg-red-100 text-red-800"
                            : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {t.status}
                    </span>
                  </button>
                ))}
                {!tickets.length && (
                  <div className="rounded bg-surface-0 p-3 text-center text-[11px] text-text-secondary">
                    No tickets yet. Scan a card above to create the first.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── RIGHT · Borang Penggredan ──────────────────────── */}
        <section className="rounded border border-border bg-surface-2">
          <div className="border-b border-border px-3 py-2 font-medium">
            Borang Penggredan · Ramp Grading
          </div>
          <div className="space-y-3 p-3">
            {!selectedTicket ? (
              <div className="rounded bg-surface-0 p-4 text-center text-[11px] text-text-secondary">
                Pick a ticket on the left to start grading. Grading records the
                first &amp; second grading counts and computes GOER per MPOB
                Jadual 3-10 in real time.
              </div>
            ) : !selectedTicket.boerPct ? (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                Set the base rate first (enter avg bunch weight and click{" "}
                <b>Save weights + base rate</b> on the left).
              </div>
            ) : (
              <>
                <div className="text-[11px] text-text-secondary">
                  Grading ticket <b className="font-mono">{selectedTicket.ticketNumber}</b>{" "}
                  · BOER {selectedTicket.boerPct}% · BKER {selectedTicket.bkerPct}%
                </div>

                {/* ─── AI Ramp Grading ─────────────────────────────── */}
                <div className="rounded-lg border-2 border-dashed border-brand-green bg-brand-green/5 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-bold text-brand-green-dark">
                      AI ramp grading
                    </div>
                    <span className="text-[9px] text-text-secondary">
                      Photograph the unloaded pile
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => aiRampFileRef.current?.click()}
                    disabled={aiRampBusy}
                    className="w-full rounded-lg border-2 border-dashed border-brand-green bg-white py-4 text-center hover:bg-brand-green/10 disabled:opacity-60"
                  >
                    <div className="text-2xl">📷</div>
                    <div className="text-xs font-semibold text-brand-green-dark">
                      {aiRampBusy
                        ? "Analysing pile…"
                        : "Photograph unloaded pile"}
                    </div>
                    <div className="text-[9px] text-text-secondary">
                      AI counts masak / mengkal / busuk / kotor / lama / dura /
                      long stalk · you confirm before saving
                    </div>
                  </button>
                  <input
                    ref={aiRampFileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void runAiRampGrade(f);
                      e.target.value = "";
                    }}
                  />
                  {aiRampPhotoPreview && (aiRampBusy || aiRampResult) && (
                    <div className="overflow-hidden rounded-lg border border-border bg-white">
                      <div className="relative">
                        <img
                          src={aiRampPhotoPreview}
                          alt="Ramp pile"
                          className="block w-full max-h-48 object-cover"
                        />
                        {aiRampBusy && (
                          <div className="absolute inset-0 flex items-center justify-center bg-emerald-900/40 text-xs font-semibold text-white">
                            Analysing…
                          </div>
                        )}
                        {aiRampResult && !aiRampBusy && (
                          <div className="absolute left-2 top-2 rounded bg-emerald-700/95 px-2 py-1 text-[10px] font-bold text-white">
                            AI · {(aiRampResult.prediction.confidence * 100).toFixed(0)}%
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {aiRampResult && (
                    <div className="rounded border border-brand-green bg-white p-2 text-[10px]">
                      <div className="mb-1 font-semibold text-brand-charcoal">
                        AI suggested counts (sample {aiRampResult.prediction.sample_size})
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
                        <div>Masak: <b>{aiRampResult.prediction.masak}</b></div>
                        <div>Mengkal: <b>{aiRampResult.prediction.mengkal}</b></div>
                        <div>Busuk: <b>{aiRampResult.prediction.busuk}</b></div>
                        <div>Kosong: <b>{aiRampResult.prediction.kosong}</b></div>
                        <div>Kotor: <b>{aiRampResult.prediction.kotor}</b></div>
                        <div>Lama: <b>{aiRampResult.prediction.lama}</b></div>
                        <div>Dura: <b>{aiRampResult.prediction.dura}</b></div>
                        <div>Tangkai panjang: <b>{aiRampResult.prediction.tangkai_panjang}</b></div>
                        <div>Unfresh: <b>{aiRampResult.prediction.unfresh}</b></div>
                        <div>Wet: <b>{aiRampResult.prediction.wet}</b></div>
                      </div>
                      <button
                        onClick={applyAiToGrading}
                        className="mt-2 w-full rounded bg-brand-green py-1.5 text-[11px] font-bold text-white hover:bg-brand-green-dark"
                      >
                        Apply AI counts to Borang Penggredan ↓
                      </button>
                    </div>
                  )}
                  {aiRampMsg && (
                    <div className="rounded bg-brand-green/10 px-2 py-1 text-[10px] text-brand-green-dark">
                      {aiRampMsg}
                    </div>
                  )}
                </div>

                <label className="text-[10px] text-text-secondary">
                  Sample size ·{" "}
                  <span className="font-semibold text-brand-green-dark">
                    100 if net weight &gt; 5 t, else 50
                  </span>
                  {selectedTicket?.netAfterReturnKg && (
                    <span className="ml-1 text-text-muted">
                      (net {(Number(selectedTicket.netAfterReturnKg) / 1000).toFixed(1)} t →
                      auto {suggestedSampleSize})
                    </span>
                  )}
                  <input
                    type="number"
                    value={g.sampleSize}
                    onChange={(e) =>
                      setG({ ...g, sampleSize: Number(e.target.value) || 100 })
                    }
                    className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                  />
                </label>

                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                    Penggredan Pertama · Jumlah (B)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Masak is DERIVED · sampleSize − Σ (defects). Read-only. */}
                    <label className="text-[10px] text-text-secondary">
                      Masak (Ripe) ·{" "}
                      <span className="text-brand-green-dark font-semibold">
                        auto
                      </span>
                      <input
                        type="number"
                        readOnly
                        value={g.masak}
                        className="mt-0.5 w-full rounded border border-brand-green/50 bg-brand-green/10 px-2 py-1 text-sm font-mono font-bold text-brand-green-dark"
                        title="Auto-derived: sample size minus the sum of all defect counts"
                      />
                    </label>
                    <GradeInput
                      label="Mengkal (Underripe)"
                      value={g.mengkal}
                      onChange={(v) => setG({ ...g, mengkal: v })}
                    />
                    <GradeInput
                      label="Busuk (Rotten)"
                      value={g.busuk}
                      onChange={(v) => setG({ ...g, busuk: v })}
                      warn="reject > 30 bunches"
                    />
                    <GradeInput
                      label="Kosong (Empty)"
                      value={g.kosong}
                      onChange={(v) => setG({ ...g, kosong: v })}
                      warn="reject > 20 bunches"
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                    Penggredan Kedua · Jumlah (C)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <GradeInput
                      label="Kotor (Dirty)"
                      value={g.kotor}
                      onChange={(v) => setG({ ...g, kotor: v })}
                      warn="reject > 30 bunches"
                    />
                    <GradeInput
                      label="Lama (Old)"
                      value={g.lama}
                      onChange={(v) => setG({ ...g, lama: v })}
                    />
                    <GradeInput
                      label="Dura"
                      value={g.dura}
                      onChange={(v) => setG({ ...g, dura: v })}
                    />
                    <GradeInput
                      label="Tangkai Panjang (Long stalk)"
                      value={g.tangkaiPanjang}
                      onChange={(v) => setG({ ...g, tangkaiPanjang: v })}
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                    Muatan Basah · Jumlah (A) · bunch counts (Jadual 10)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[10px] text-text-secondary">
                      Tandan Tidak Segar (bunches)
                      <input
                        type="number"
                        min={0}
                        value={g.unfreshCount}
                        onChange={(e) =>
                          setG({
                            ...g,
                            unfreshCount: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                      />
                    </label>
                    <label className="text-[10px] text-text-secondary">
                      Muatan Basah (bunches)
                      <input
                        type="number"
                        min={0}
                        value={g.wetCount}
                        onChange={(e) =>
                          setG({
                            ...g,
                            wetCount: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-sm font-mono"
                      />
                    </label>
                  </div>
                </div>

                <div className="text-[10px] text-text-secondary">
                  Counted: <b>{totalCounted}</b> of {g.sampleSize} bunches
                </div>

                {/* Live penalty breakdown */}
                {preview && (
                  <div
                    className={`rounded border p-3 ${
                      preview.consignmentRejected
                        ? "border-red-400 bg-red-50"
                        : "border-emerald-300 bg-emerald-50"
                    }`}
                  >
                    {preview.consignmentRejected ? (
                      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-red-800">
                        <IconAlertTriangle size={16} />
                        Whole consignment REJECTED · {preview.rejectRuleFired}
                      </div>
                    ) : (
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                        Live penalty preview
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div className="space-y-1 font-mono">
                        {Object.entries(preview.perClassPct)
                          .filter(([, v]) => v > 0)
                          .map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                              <span className="text-text-secondary">{k}</span>
                              <span className="tabular-nums">
                                {v.toFixed(2)}%
                              </span>
                            </div>
                          ))}
                      </div>
                      <div className="space-y-1 text-right">
                        <div className="text-[10px] text-text-secondary">
                          Total penalty
                        </div>
                        <div className="font-mono text-sm font-bold">
                          {preview.totalPenaltyPct.toFixed(2)}%
                        </div>
                        <div className="text-[10px] text-text-secondary">
                          GOER (Digred)
                        </div>
                        <div className="font-mono text-lg font-bold text-emerald-800">
                          {preview.goerPct.toFixed(2)}%
                        </div>
                        <div className="text-[10px] text-text-secondary">
                          GKER
                        </div>
                        <div className="font-mono text-sm">
                          {preview.gkerPct.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={saveGrading}
                  disabled={totalCounted <= 0}
                  className="w-full rounded bg-text-primary py-2 text-sm font-bold text-white disabled:opacity-40"
                >
                  Commit grading + close ticket
                </button>
                {gradeMsg && (
                  <div
                    className={`rounded p-2 text-xs ${
                      gradeMsg.includes("✓")
                        ? "border border-emerald-300 bg-emerald-50 text-emerald-800"
                        : "border border-amber-300 bg-amber-50 text-amber-900"
                    }`}
                  >
                    {gradeMsg}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function GradeInput({
  label,
  value,
  onChange,
  warn,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  warn?: string;
}) {
  return (
    <label className="text-[10px] text-text-secondary">
      {label}
      {warn && <span className="ml-1 text-[9px] text-red-700">· {warn}</span>}
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="mt-0.5 w-full rounded border border-border bg-surface-0 px-2 py-1 text-right text-sm font-mono"
      />
    </label>
  );
}

function Kpi({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded border border-border bg-surface-2 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-text-secondary">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums text-text-primary">
        {value}
      </div>
      {hint && <div className="text-[10px] text-text-secondary">{hint}</div>}
    </div>
  );
}
